// Static batching (RALLY_GOAL.md §14 CPU budget): many small meshes sharing a material become one
// mesh per material, baked into the root's space. Each WebGPU draw costs CPU time per pass (main
// pass plus every shadow cascade), so this matters more than triangle count here.
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { attribute } from 'three/tsl';

/** Rebuild position/normal/uv as plain Float32 (glTF quantised attributes can't be merged as is). */
function plain(g: THREE.BufferGeometry, m: THREE.Matrix4): THREE.BufferGeometry {
  const src = g;
  const out = new THREE.BufferGeometry();
  const pos = src.attributes.position;
  const n = pos.count;
  const p = new Float32Array(n * 3);
  const nr = new Float32Array(n * 3);
  const uv = new Float32Array(n * 2);
  const nor = src.attributes.normal;
  const tuv = src.attributes.uv;
  for (let i = 0; i < n; i++) {
    p[i * 3] = pos.getX(i);
    p[i * 3 + 1] = pos.getY(i);
    p[i * 3 + 2] = pos.getZ(i);
    if (nor) {
      nr[i * 3] = nor.getX(i);
      nr[i * 3 + 1] = nor.getY(i);
      nr[i * 3 + 2] = nor.getZ(i);
    } else nr[i * 3 + 1] = 1;
    if (tuv) {
      uv[i * 2] = tuv.getX(i);
      uv[i * 2 + 1] = tuv.getY(i);
    }
  }
  out.setAttribute('position', new THREE.BufferAttribute(p, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(nr, 3));
  out.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  if (src.index) out.setIndex(Array.from(src.index.array as ArrayLike<number>));
  else out.setIndex(Array.from({ length: n }, (_, i) => i));
  out.applyMatrix4(m);
  // A mirrored transform flips winding; keep faces front-facing.
  if (m.determinant() < 0) {
    const idx = out.index!.array as Uint32Array | Uint16Array;
    for (let i = 0; i < idx.length; i += 3) {
      const t = idx[i + 1];
      idx[i + 1] = idx[i + 2];
      idx[i + 2] = t;
    }
  }
  return out;
}

/**
 * Merge the meshes under `root` (excluding subtrees for which `keep` returns true) into one mesh
 * per material, parented to `root`. `shadow(material)` decides which merged meshes cast shadows.
 */
export function batchByMaterial(root: THREE.Object3D, keep: (o: THREE.Object3D) => boolean, shadow: (m: THREE.Material) => boolean): number {
  root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const groups = new Map<THREE.Material, THREE.BufferGeometry[]>();
  const victims: THREE.Mesh[] = [];
  const walk = (o: THREE.Object3D) => {
    if (o !== root && keep(o)) return;
    const mesh = o as THREE.Mesh;
    // Skip meshes that are already batches (a flat batch carries custom attributes plain() drops).
    if (mesh.isMesh && !mesh.name.startsWith('batch:') && !Array.isArray(mesh.material) && !(mesh as THREE.InstancedMesh).isInstancedMesh) {
      const rel = new THREE.Matrix4().multiplyMatrices(inv, mesh.matrixWorld);
      let list = groups.get(mesh.material);
      if (!list) groups.set(mesh.material, (list = []));
      list.push(plain(mesh.geometry, rel));
      victims.push(mesh);
    }
    for (const c of o.children) walk(c);
  };
  walk(root);
  for (const v of victims) v.removeFromParent();
  let n = 0;
  for (const [mat, geos] of groups) {
    const merged = mergeGeometries(geos, false);
    if (!merged) continue;
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, mat);
    mesh.name = `batch:${mat.name}`;
    mesh.castShadow = shadow(mat);
    mesh.receiveShadow = true;
    root.add(mesh);
    n++;
  }
  return n;
}

/** A plain-coloured standard material (no maps, no custom colour/emissive nodes). */
export function isFlat(m: THREE.Material): boolean {
  const s = m as THREE.MeshStandardNodeMaterial & { isMeshPhysicalNodeMaterial?: boolean };
  return !!s.isMeshStandardNodeMaterial && !s.isMeshPhysicalNodeMaterial && !s.map && !s.colorNode && !s.emissiveNode && !s.transparent;
}

/**
 * Merge every flat-coloured mesh under `root` (outside `keep` subtrees) whose material passes
 * `select` into a single mesh; colour, roughness and metalness travel as vertex attributes, so
 * parts with different materials still cost one draw.
 */
export function batchFlat(root: THREE.Object3D, keep: (o: THREE.Object3D) => boolean, select: (m: THREE.Material) => boolean, castShadow: boolean, name = 'flat'): THREE.Mesh | null {
  root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const geos: THREE.BufferGeometry[] = [];
  const victims: THREE.Mesh[] = [];
  let doubleSided = false;
  const walk = (o: THREE.Object3D) => {
    if (o !== root && keep(o)) return;
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh && !mesh.name.startsWith('batch:') && !Array.isArray(mesh.material) && isFlat(mesh.material) && select(mesh.material)) {
      const m = mesh.material as THREE.MeshStandardNodeMaterial;
      const g = plain(mesh.geometry, new THREE.Matrix4().multiplyMatrices(inv, mesh.matrixWorld));
      const n = g.attributes.position.count;
      const fc = new Float32Array(n * 4);
      const fm = new Float32Array(n);
      for (let i = 0; i < n; i++) {
        fc[i * 4] = m.color.r;
        fc[i * 4 + 1] = m.color.g;
        fc[i * 4 + 2] = m.color.b;
        fc[i * 4 + 3] = m.roughness;
        fm[i] = m.metalness;
      }
      g.setAttribute('fcol', new THREE.BufferAttribute(fc, 4));
      g.setAttribute('fmet', new THREE.BufferAttribute(fm, 1));
      geos.push(g);
      victims.push(mesh);
      if (m.side === THREE.DoubleSide) doubleSided = true;
    }
    for (const c of o.children) walk(c);
  };
  walk(root);
  if (!geos.length) return null;
  for (const v of victims) v.removeFromParent();
  const merged = mergeGeometries(geos, false);
  if (!merged) return null;
  merged.computeBoundingSphere();
  const mat = new THREE.MeshStandardNodeMaterial({ side: doubleSided ? THREE.DoubleSide : THREE.FrontSide });
  mat.name = name;
  const fcol = attribute('fcol', 'vec4');
  mat.colorNode = fcol.rgb;
  mat.roughnessNode = fcol.a;
  mat.metalnessNode = attribute('fmet', 'float');
  const mesh = new THREE.Mesh(merged, mat);
  mesh.name = `batch:${name}`;
  mesh.castShadow = castShadow;
  mesh.receiveShadow = true;
  root.add(mesh);
  return mesh;
}
